// Portfolio product weights are percentages stored with 4 decimals. They are
// summed as integer ten-thousandths to avoid float drift, and a total counts as
// "100%" when it is within 0.01 of FULL_WEIGHT_TEN_THOUSANDTHS.

import { productFitsDynamicPortfolioPlatforms } from "./platform-fit";

/**
 * The factor that rounds a percentage to 4 decimals, the precision weights and
 * allocations are stored at: `Math.round(value * PERCENT_ROUNDING_SCALE) /
 * PERCENT_ROUNDING_SCALE`. Also the number of ten-thousandths in one percent.
 */
export const PERCENT_ROUNDING_SCALE = 10_000;

export const FULL_WEIGHT_TEN_THOUSANDTHS = 100 * PERCENT_ROUNDING_SCALE;

// 0.01%, the tolerance asset allocations and combined instruments also use.
const WEIGHT_TOTAL_TOLERANCE_TEN_THOUSANDTHS = 100;

export const toTenThousandths = (weight: number): number =>
  Math.round(weight * PERCENT_ROUNDING_SCALE);

/**
 * QL-584 — the highest a dynamic portfolio's preference maximum can be set, in
 * percent. It bounds both the maximum per preference and the one for all.
 */
export const PREFERENCE_MAXIMUM_LIMIT = 50;

/** True when `weight` has at most 4 decimals, the precision weights are stored at. */
export const hasStoredPrecision = (weight: number): boolean => Number(weight.toFixed(4)) === weight;

/** Integer ten-thousandths total of a weight list. */
export function sumTenThousandths(weights: number[]): number {
  return weights.reduce((sum, weight) => sum + toTenThousandths(weight), 0);
}

/** The total of a weight list in percent, free of float drift. */
export function sumWeights(weights: number[]): number {
  return sumTenThousandths(weights) / PERCENT_ROUNDING_SCALE;
}

/**
 * QL-584 — a portfolio is complete when it has products and their weights
 * total within 0.01 of 100. Only a complete dynamic portfolio is generated in
 * a flow, and generation closes the gap so the result totals exactly 100.
 */
export function isWeightTotalComplete(weights: number[]): boolean {
  return (
    weights.length > 0 &&
    Math.abs(sumTenThousandths(weights) - FULL_WEIGHT_TEN_THOUSANDTHS) <
      WEIGHT_TOTAL_TOLERANCE_TEN_THOUSANDTHS
  );
}

/**
 * Whether a product can carry a weight. A product ordered in units is bought
 * by count, so no weighted portfolio (model or dynamic) may hold it.
 */
export function isWeightBasedProduct(product: { orderAmountType: "amount" | "units" }): boolean {
  return product.orderAmountType !== "units";
}

/**
 * QL-584 — the part of what a dynamic portfolio may hold that does not depend
 * on its platforms: an active, weight-based product.
 */
export function isHoldableByDynamicPortfolio(product: {
  isActive: boolean;
  orderAmountType: "amount" | "units";
}): boolean {
  return product.isActive && isWeightBasedProduct(product);
}

/**
 * QL-584 — whether a product can be added to a dynamic portfolio with this
 * platform membership: holdable and on all of its platforms. What the saves
 * enforce and what the settings UI offers.
 */
export function canAddToDynamicPortfolio(
  product: { isActive: boolean; orderAmountType: "amount" | "units"; platformIds: string[] },
  portfolioPlatformIds: string[],
): boolean {
  return (
    isHoldableByDynamicPortfolio(product) &&
    productFitsDynamicPortfolioPlatforms(product.platformIds, portfolioPlatformIds)
  );
}
